/* eslint-disable consistent-return, no-restricted-globals, no-else-return, no-unused-vars, no-nested-ternary, prefer-template */

import {
  CountrySelect,
  DropDown,
  InputField,
  PhoneNumber,
  PrimaryButton,
  SelectAutoComplete,
  TeamCodeComponent,
  ToggleGroup,
} from '@gameday/react-components/components'
import { makeStyles } from '@material-ui/core/styles'
import classNames from 'classnames'
import { Formik } from 'formik'
import FormikErrorFocus from 'formik-error-focus'
import { parsePhoneNumberFromString } from 'libphonenumber-js/min'
import { get, isArray, isBoolean, isEmpty, isFunction, isNil, isObject, isString } from 'lodash'
import moment from 'moment'
import { postcodeValidator, postcodeValidatorExistsForCountry } from 'postcode-validator'
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { array, boolean, date, mixed, number, object, string } from 'yup'
import { getCountryCode, phoneValidationTest } from '../functions'
import { isUnknownDob } from '../functions/dob'
import { sanitizeRichText } from '../functions/richText'
import Address from './components/address'
import Checkbox from './components/checkbox'
import DatePicker from './components/datePicker'
import DateOfBirth from './components/dob'
import FileUpload from './components/fileUpload'
import ImageUpload from './components/imageUpload'
import MemberType from './components/memberType'
import PickList from './components/picklist'
import Tnc from './components/tnc'
import { displayRuleFilter } from './filter'
import styles from './styles.module.scss'

// biome-ignore lint/correctness/noUnusedVariables: TODO
const useStyles = makeStyles(theme => ({
  root: {
    fontSize: '13px',
    fontFamily: ['Barlow', 'sans-serif'].join(','),
  },
}))

const StyledForm = styled.div`
  .bordered .custom-textfield:focus-within,
  .PhoneNumber-singleInput:focus-within,
  .SingleDatePickerInput__withBorder:focus-within,
  .Mui-focused input {
    border-color: var(--custom-primary-color);
  }
  word-break: break-word;
  white-space: break-spaces;
`

const StyledNoResult = styled.div`
  font-size: 1.5em;
  font-weight: bold;
  text-align: center;
  width: 100%;
`
const StyledTnCWrapper = styled.div`
  width: 100%;
  float: left;
  margin-top: 14px;
  margin-bottom: 10px;
  font-size: 15px;
  font-weight: 500;
  color: var(--gameday-body-headingColor);
  hr {
    border: none;
    border-bottom: 1px solid var(--gameday-colors-border);
    margin: 6px 0 16px;
  }
`

const isParentQuestionFulfilled = (questions = [], values = {}) => {
  if ((questions || []).length < 1) {
    return true
  }
  return questions.every(q => {
    const qId = q.primaryQuestionId
    const pValue = values[qId]
    const expAnswer = q.primaryQuestionAnswer
    return isAnswerMatch(pValue, expAnswer)
  })
}

const isAnswerMatch = (value, answer) => {
  if (isArray(value) || isObject(value)) {
    return Object.values(value).includes(answer) || Object.keys(value).includes(answer)
  }
  if (isBoolean(value)) {
    return !isNil(value) && String(value).toLowerCase() === String(answer).toLowerCase()
  }
  return !!value && value === answer
}

const getPhoneNumberObject = phoneNumber => {
  if (isString(phoneNumber) && phoneNumber) {
    const validation = parsePhoneNumberFromString(phoneNumber) || {}
    return {
      value: validation.number,
      countryCode: validation.country,
    }
  }
  return (
    phoneNumber || {
      value: '',
      countryCode: '',
    }
  )
}

const generateNumberQuestionSchema = question => {
  const q = question.relatedQuestion
  /* eslint-disable-next-line prefer-const */
  const numberSchema = string()
    .test({
      name: q.text,
      message: 'Value must be a number',
      test(value) {
        if (!value) {
          return true
        }
        // biome-ignore lint/suspicious/noGlobalIsNan: TODO
        const isNumeric = !isNaN(value)
        if (!isNumeric || !value.trim()) {
          return false
        }
        return true
      },
    })
    .test({
      name: q.text,
      message: `The length of digits must be longer or equal to ${q.minNumDigits} digits`,
      test(value) {
        if (!value || !q.minNumDigits) {
          return true
        }
        const numericForm = value.replace(/\D\./g, '').split('.')[0]
        if (numericForm.length < q.minNumDigits) {
          return false
        }
        return true
      },
    })
    .test({
      name: q.text,
      message: `The length of digits must be shorter or equal to ${q.maxNumDigits} digits`,
      test(value) {
        if (!value || !q.maxNumDigits) {
          return true
        }
        const numericForm = value.replace(/\D\./g, '').split('.')[0]
        if (numericForm.length > q.maxNumDigits) {
          return false
        }
        return true
      },
    })
  return numberSchema
}

const generatePhoneNumberSchema = question => {
  const q = question.relatedQuestion
  const { countryCode } = this?.parent || {}
  let numberSchema = string().label(q.text).test(phoneValidationTest(q.text, countryCode))

  if (question.isMandatory) {
    numberSchema = numberSchema.required()
  }

  return object({
    value: numberSchema,
    countryCode: string(),
  })
}

const teamNameQuestionValidation = question => {
  const q = question.relatedQuestion
  let teamNameSchema = string()
    .label(q.text)
    .test({
      name: q.text,
      message: 'Team Name cannot contain special characters',
      test(value) {
        if (!value) {
          return true
        }
        const regex = /^[0-9a-zA-Z ]+$/
        return value && regex.test(value)
      },
    })

  if (question.isMandatory) {
    teamNameSchema = teamNameSchema.required()
  }

  return teamNameSchema
}

const teamCodeQuestionValidation = question => {
  const q = question.relatedQuestion
  let teamCodeSchema = string()
    .label(q.text)
    .test({
      name: q.text,
      message: 'Team Code cannot contain special characters',
      test(value) {
        if (!value) {
          return true
        }
        const regex = /^[0-9a-zA-Z]+$/
        return value && regex.test(value)
      },
    })

  if (question.isMandatory) {
    teamCodeSchema = teamCodeSchema.required()
  }

  return teamCodeSchema
}

const validateTeamCode = (question, teamCodes) => {
  const q = question.relatedQuestion
  let teamCodeSchema = string()
    .label(q.text)
    .test({
      name: q.text,
      message: 'The Team Code you have entered is already taken. Please try another.',
      test(value) {
        if (!value) {
          return true
        }
        return value && !teamCodes.find(t => t.teamCode.toLowerCase() === value.toLowerCase())
      },
    })
    .test({
      name: q.text,
      message: 'Team Code cannot contain special characters',
      test(value) {
        if (!value) {
          return true
        }
        const regex = /^[0-9a-zA-Z]+$/
        return value && regex.test(value)
      },
    })

  if (question.isMandatory) {
    teamCodeSchema = teamCodeSchema.required()
  }

  return teamCodeSchema
}

const validateTeamsInEvent = (question, teamsInEvent) => {
  const q = question.relatedQuestion
  let teamInEventSchema = mixed()
    .label(q.text)
    .test({
      name: q.text,
      message: 'The Team you have selected has already been entered into this event. Please select another team.',
      test(value) {
        const id = typeof value === 'number' ? value : value?.id
        return value && !teamsInEvent.find(t => t.teamId === id)
      },
    })

  if (question.isMandatory) {
    teamInEventSchema = teamInEventSchema.required()
  }

  return teamInEventSchema
}

const generateValidationSchema = (questions = [], teamCodes = [], teamsInEvent = []) => {
  const schema = questions.reduce((a, question) => {
    let obj
    const q = question.relatedQuestion || {}
    switch (q.type || '') {
      case 'Image Upload':
      case 'Text':
      case 'Picklist':
        // case 'User':
        obj = string().nullable()
        if (['Firstname', 'Lastname'].includes(q.name)) {
          obj = obj.trim().required().max(100)
        } else {
          obj = obj.max(500)
        }
        if (q.name === 'Email' || q.name === 'Team Email' || q.name === 'Email Confirmation') {
          obj = obj.email('Please enter a valid email address. (abc@example.com)').max(80)
        }
        if (q.isSportsTGQuestion && q.name === 'Email Confirmation') {
          obj = string()
            .label(q.text)
            .test({
              name: q.text,
              message: 'Email confirmation does not match.',
              test(value) {
                const emailQ = questions.find(q => q.relatedQuestion.name === 'Email').relatedQuestion
                const emailValue = this.parent[emailQ.sfid]
                return value === emailValue
              },
            })
        }
        if (q.isSportsTGQuestion && q.name.includes('Address')) {
          let inner = {
            city: string().label('City'),
            country: string().label('Country'),
            postalcode: string()
              .label('Postal Code')
              .max(20, 'Postal Code cannot be more than 20 characters.')
              .test('PostalCodeValidation', 'Invalid Postal Code', function (value) {
                const {
                  path,
                  createError,
                  parent: { country = '' },
                } = this
                const countryCode = getCountryCode(country)

                if (postcodeValidatorExistsForCountry(countryCode) && !postcodeValidator(value, countryCode)) {
                  return createError({ path, message: 'Invalid Postal Code' })
                }

                return true
              }),
            state: string().label('State/Province'),
            street: string().label('Street'),
          }
          if (question.isMandatory) {
            inner = Object.keys(inner).reduce(
              (a, i) => ({
                // biome-ignore lint/performance/noAccumulatingSpread: TODO
                ...a,
                [i]: inner[i].required(),
              }),
              {}
            )
          }
          obj = object(inner)
        }
        if (q.isSportsTGQuestion && q.name === 'Mobile Number') {
          obj = generatePhoneNumberSchema(question)
        }
        if (q.isSportsTGQuestion && q.name === 'Team Name for Meet Manager') {
          obj = teamNameQuestionValidation(question).max(30)
        } else if (q.isSportsTGQuestion && q.name === 'Team Code for Meet Manager') {
          obj = teamCodeQuestionValidation(question).max(4)
        }
        if (q.isSportsTGQuestion && q.name === 'Team Code') {
          obj = validateTeamCode(question, teamCodes)
        }
        if (q.isSportsTGQuestion && q.name === 'Team Name') {
          obj = teamNameQuestionValidation(question)
        }
        break
      case 'Number':
        obj = number()
        if (q.isSportsTGQuestion) {
          obj = generatePhoneNumberSchema(question)
        } else {
          obj = generateNumberQuestionSchema(question)
        }
        break
      case 'Date':
        obj = date()
        break
      case 'Document Upload':
      case 'MultiMemberType':
        obj = array().nullable()
        break
      case 'multiPicklist':
        obj = mixed().nullable()
        break
      case 'TermsAndConditions':
      case 'Checkbox':
        obj = boolean()
        break
      case 'DropDown':
        if (q.isSportsTGQuestion && q.isTeamQuestion && q.name === 'Select Existing Team') {
          obj = validateTeamsInEvent(question, teamsInEvent)
        } else {
          obj = string()
        }
        break
      case 'Search':
        obj = mixed()
        break
      case 'Header Text':
      case 'Paragraph Text':
        return a
      default:
        obj = mixed()
        break
    }

    if (isFunction(obj.label)) {
      obj = obj.label(q.text)
    }
    if (question.isMandatory) {
      if (['TermsAndConditions', 'Checkbox'].includes(q.type)) {
        obj = obj.oneOf([true], `${q.text} is required`)
      }
      if (isFunction(obj.required)) {
        obj = obj.required()
      }
    }
    if ((question.dependentQuestions || []).length > 0) {
      const mapped = question.dependentQuestions.reduce(
        (a, dp) => ({
          // biome-ignore lint/performance/noAccumulatingSpread: TODO
          ...a,
          [dp.primaryQuestionId]: dp.primaryQuestionAnswer,
        }),
        {}
      )
      const keys = Object.keys(mapped)
      const values = Object.values(mapped)
      obj = mixed().when(keys, {
        is: (...args) => !args.some((answer, index) => !isAnswerMatch(answer, values[index])),
        // biome-ignore lint/suspicious/noThenProperty: TODO
        then: obj,
      })
    }
    a[q.sfid] = obj
    return a
  }, {})

  return object(schema)
}

function QuestionForm({
  questions,
  formProducts,
  formData,
  onSubmit = () => {},
  bindHandler,
  defaultValue,
  memberTypes,
  disableQuestions,
  disableMemberType,
  setPending = () => {},
  defaultCountry,
  hierarchyCountryCode,
  onChange = () => {},
  onTouched = () => {},
  searchOnClick = () => {},
  searchValues,
  teamCodes,
  teamsInEvent,
  teamsByCode,
  _teamsByCodeInEvent,
  formType,
}) {
  const classes = useStyles()
  const [initValue, setInitValue] = useState(defaultValue)
  const [searchValue, setSearchValue] = useState('')
  const [teamValueSelected, setTeamValueSelected] = useState({})
  const [childQuestionMap, setChildQuestionMap] = useState({})
  const [searchClicked, setSearchClicked] = useState(false)
  const country = get(defaultCountry, 'name')
  const filteredQuestions = !isEmpty(questions)
    ? displayRuleFilter(
        (questions || []).map(q => ({ ...q, org: q.definedBy })),
        formData,
        formProducts
      ).filter(q => !disableMemberType || q.relatedQuestion?.type !== 'MultiMemberType')
    : []

  const handleSearchKeyPress = e => {
    if (e.key === 'Enter') {
      searchOnClick(searchValue)
    }
  }

  useEffect(() => {
    if (filteredQuestions.length < 1) {
      onSubmit(null)
    }
  }, [filteredQuestions])

  useEffect(() => {
    const tempChildQuestionMap = filteredQuestions.reduce((a, question) => {
      const depQuestions = question.dependentQuestions || []
      const questionId = get(question, 'relatedQuestion.sfid')
      if (depQuestions.length < 1) {
        return a
      }

      depQuestions.forEach(depQ => {
        const depQId = depQ.primaryQuestionId
        a[depQId] = [
          ...(a[depQId] || []),
          {
            id: questionId,
            value: depQ.primaryQuestionAnswer,
          },
        ]
      })
      return a
    }, {})
    // biome-ignore lint/performance/noAccumulatingSpread: TODO
    const fileterdQuestionMap = filteredQuestions.reduce((a, q) => ({ ...a, [q.relatedQuestion.sfid]: q }), {})
    const tempInitValue = Object.keys(defaultValue).reduce((a, key) => {
      const question = fileterdQuestionMap[key]
      if (question && !(key in a)) {
        const q = question.relatedQuestion || {}
        if (q.isSportsTGQuestion && (q.type === 'Number' || q.name === 'Mobile Number')) {
          a[key] = getPhoneNumberObject(defaultValue[key])
          return a
        }
        if (q.type === 'Checkbox' || q.type === 'TermsAndConditions') {
          a[key] = !!defaultValue[key]
          return a
        }
        if (q.type === 'Header Text' || q.type === 'Paragraph Text') {
          return a
        }
        a[key] = defaultValue[key]
        return a
      }
      a[key] = undefined
      const cqList = tempChildQuestionMap[key] || []
      cqList.forEach(cq => {
        a[cq.id] = undefined
      })
      return a
    }, {})
    setChildQuestionMap(tempChildQuestionMap)
    setInitValue(tempInitValue)
  }, [defaultValue, questions, formData])

  useEffect(() => {
    if (defaultValue.inputTeamCode) {
      searchOnClick(defaultValue.inputTeamCode)
    }
  }, [defaultValue])

  const validationSchema = generateValidationSchema(filteredQuestions, teamCodes, teamsInEvent)

  const termsAndConditionsCount = filteredQuestions.filter(q => q.relatedQuestion.type === 'TermsAndConditions')

  const renderComponents = ({ setFieldValue, setFieldTouched, setTouched, errors, touched, values }) => {
    if (isFunction(onChange)) {
      onChange(values)
    }

    const onDataChange = (key, val, setTouched) => {
      if (setTouched) {
        const args = [key, ...(isObject(val) ? Object.keys(val) : [])]
        onFieldTouched(...args)
      }
      setFieldValue(key, val)
      if (isFunction(onTouched)) {
        onTouched(key)
      }
      if (!childQuestionMap[key] || childQuestionMap[key].length < 1) {
        return
      }
      childQuestionMap[key].forEach(q => {
        if (!isAnswerMatch(val, q.value)) {
          setFieldValue(q.id, undefined, false)
        }
      })
    }

    const onFieldTouched = (key, ...subKeys) => {
      const newTouched = { ...touched }
      if (subKeys.length > 0) {
        // biome-ignore lint/performance/noAccumulatingSpread: TODO
        newTouched[key] = subKeys.reduce((a, k) => ({ ...a, [k]: true }), {})
        return setTouched(newTouched)
      }
      setFieldTouched(key)
    }

    return filteredQuestions.map(question => {
      if (!isParentQuestionFulfilled(question.dependentQuestions, values)) {
        return null
      }
      const q = question.relatedQuestion
      const value = get(values, q?.sfid)
      let errorText = get(touched, q?.sfid) && get(errors, q?.sfid)
      // biome-ignore lint/style/useTemplate: TODO
      const dataTestId = q?.text?.replaceAll(/\s/g, '') + 'Field'

      if (disableQuestions && q.name === 'Register or Join a Team') {
        errorText = 'You have already registered a team to this event. You cannot register multiple teams.'
      } else if (!isEmpty(teamsByCode) && isEmpty(searchValues) && q.name === 'Input Team Code') {
        errorText = 'The team with your entered team code is not yet registered to this event.'
      } else if (value && isEmpty(teamsByCode) && isEmpty(searchValues) && q.name === 'Input Team Code') {
        errorText = 'We cannot find a registered team with the entered team code. Please try again.'
      }

      switch (q.type) {
        case 'Image Upload':
          return (
            <ImageUpload
              key={q.sfid}
              formData={formData}
              question={q}
              name={q.sfid}
              onChange={val => {
                onDataChange(q.sfid, val, true)
              }}
              required={(question.text || q.text) === 'Team Logo' ? false : question.isMandatory}
              value={value}
              errorText={errorText}
              id={q.sfid}
              isToolTip={!!question.questionHelpText}
              toolTipText={question.questionHelpText}
            />
          )
        case 'Text':
        case 'Number':
          if (
            (q.isSportsTGQuestion &&
              (q.type === 'Number' || q.name === 'Emergency Contact Number' || q.name === 'Mobile Number')) ||
            q.name === 'Team Mobile Number'
          ) {
            return (
              <div key={q.sfid} className={styles.nameContainer}>
                <PhoneNumber
                  defaultCountry={country}
                  // biome-ignore lint/complexity/useOptionalChain: TODO
                  errorText={(errorText || {}).value}
                  fullWidth
                  name={q.sfid}
                  label={question.text || q.text}
                  isRequired={question.isMandatory}
                  onBlur={() => onFieldTouched(q.sfid, 'value', 'countryCode')}
                  onNumberChange={val => onDataChange(q.sfid, val)}
                  placeholder="Please enter..."
                  value={value || ''}
                  withBorder
                  isToolTip={!!question.questionHelpText}
                  toolTipText={question.questionHelpText}
                  id={q.sfid}
                  dataTestId={dataTestId}
                />
              </div>
            )
          }
          if (q.isSportsTGQuestion && q.name.includes('Address')) {
            return (
              <Address
                key={q.sfid}
                errorText={errorText}
                id={q.sfid}
                label={question.text || q.text}
                name={q.sfid}
                isToolTip={!!question.questionHelpText}
                toolTipText={question.questionHelpText}
                isRequired={question.isMandatory}
                onChange={val => onDataChange(q.sfid, val)}
                values={value || initValue[q.sfid]}
                endpoint={formData.subdivisionEndpoint}
                googleApiKey={formData.googleApiKey}
                hierarchyCountryCode={hierarchyCountryCode}
                dataTestId={dataTestId}
              />
            )
          }
          if (!q.isSportsTGQuestion && q.name !== 'Emergency Contact Number' && q.name !== 'Mobile Number') {
            return (
              <div key={q.sfid} className={styles.nameContainer}>
                <InputField
                  label={question.text || q.text}
                  name={q.sfid}
                  disabled={disableQuestions}
                  isRequired={question.isMandatory}
                  withBorder
                  placeholder={(q => {
                    const { minNumDigits, maxNumDigits } = q
                    if (!!minNumDigits && !!maxNumDigits) {
                      return `Please enter a number with a length between ${minNumDigits} and ${maxNumDigits} digits`
                    } else if (!!minNumDigits && !maxNumDigits) {
                      return `Please enter a number with a length greater than ${minNumDigits} digits`
                    } else if (!minNumDigits && !!maxNumDigits) {
                      return `Please enter a number with a length less than ${maxNumDigits} digits`
                    } else {
                      return 'Please enter...'
                    }
                  })(q)}
                  onChange={event => onDataChange(q.sfid, event.target.value)}
                  value={value}
                  errorText={errorText}
                  onBlur={() => onFieldTouched(q.sfid)}
                  fullwidth
                  isToolTip={!!question.questionHelpText}
                  toolTipText={question.questionHelpText}
                  id={q.sfid}
                />
              </div>
            )
          }
          return (
            <div key={q.sfid} className={styles.nameContainer}>
              <InputField
                type={q.type.toLowerCase()}
                label={
                  (q?.text?.toLowerCase() || question?.text?.toLowerCase()) === 'primary contact name'
                    ? 'Team Primary Contact (Full Name)'
                    : (q?.text?.toLowerCase() || question?.text?.toLowerCase()) === 'team primary contact'
                      ? 'Team Primary Contact (Full Name)'
                      : question.text || q.text
                }
                name={q.sfid}
                disabled={
                  disableQuestions &&
                  !(q.isSportsTGQuestion && ['Email', 'Email Confirmation', 'Gender Identity - Other'].includes(q.name))
                }
                isRequired={question.isMandatory}
                withBorder
                placeholder="Please enter..."
                value={value}
                errorText={errorText}
                onBlur={() => onFieldTouched(q.sfid)}
                onChange={event => onDataChange(q.sfid, event.target.value)}
                fullwidth
                isToolTip={!!question.questionHelpText}
                toolTipText={question.questionHelpText}
                id={q.sfid}
                dataTestId={dataTestId}
              />
            </div>
          )
        case 'Date':
          return (
            <div key={q.sfid} className={styles.nameContainer}>
              {['salesforce.member__c', 'salesforce.memberprofile__c'].includes(q.mappingTable) &&
              q.mappingField === 'birthdate__c' ? (
                <DateOfBirth
                  label={q.text || q.text}
                  name={q.sfid}
                  required={question.isMandatory}
                  date={moment(value, 'YYYY-MM-DD').format('YYYY-MM-DD')}
                  disabled={isUnknownDob(initValue[q.sfid]) ? false : disableQuestions}
                  errorText={errorText}
                  onChange={date => onDataChange(q.sfid, date, true)}
                  isSportsTGQuestion={q.isSportsTGQuestion || false}
                  id={q.sfid}
                  dataTestId={dataTestId}
                />
              ) : (
                <DatePicker
                  label={q.text || q.text}
                  name={q.sfid}
                  required={question.isMandatory}
                  date={moment(value, 'YYYY-MM-DD').format('YYYY-MM-DD')}
                  disabled={disableQuestions}
                  errorText={errorText}
                  onChange={date => onDataChange(q.sfid, date, true)}
                  isSportsTGQuestion={q.isSportsTGQuestion || false}
                  id={q.sfid}
                  isToolTip={!!question.questionHelpText}
                  toolTipText={question.questionHelpText}
                  dataTestId={dataTestId}
                />
              )}
            </div>
          )
        case 'Toggle':
          return (
            // biome-ignore lint/correctness/useJsxKeyInIterable: TODO
            <ToggleGroup
              label={question.text || q.text}
              toggles={question.options}
              default={value || question.defaultValue}
              isMandatory={question.isMandatory}
              disabled={disableQuestions && q.name === 'Register or Join A Team'}
              errorText={errorText}
              helpText={question.questionHelpText}
              id={q.sfid}
              onBlur={() => onFieldTouched(q.sfid)}
              onChange={data => {
                onDataChange(q.sfid, data, true)
              }}
              fullWidth
              fullWidthToggle
              classes={{ root: classes.root }}
            />
          )
        case 'DropDown':
          return (
            <>
              <DropDown
                isLabelInline={false}
                isRequired={question.isMandatory}
                label={question.text || q.text}
                defaultOption
                defaultValue="Please select"
                onChange={args => {
                  onDataChange(q.sfid, args, true)
                  if (q.isTeamQuestion) {
                    setTeamValueSelected(question.options[0].value.filter(o => o.id === args)[0])
                  }
                }}
                selected={value || initValue[q.sfid]}
                filterData={question.options}
                errorText={errorText}
                withBorder
                fullWidth
                dataTestId={dataTestId}
              />
              {q.isTeamQuestion && !isEmpty(teamValueSelected) && <TeamCodeComponent team={teamValueSelected} />}
            </>
          )
        case 'MultiMemberType':
          return (
            <MemberType
              key={q.sfid}
              errorText={errorText}
              label={formType === 'event' ? 'Which entry type are you registering as?' : question.text || q.text}
              name={q.sfid}
              memberTypes={memberTypes}
              onChange={types => onDataChange(q.sfid, types, true)}
              required={question.isMandatory}
              value={value || initValue[q.sfid]}
              id={q.sfid}
              isToolTip={!!question.questionHelpText}
              toolTipText={question.questionHelpText}
              allowOnlyOneMemberType={formData.allowOnlyOneMemberType}
              heading={formType === 'event' ? 'Entry Type' : undefined}
            />
          )
        case 'Picklist':
        case 'multiPicklist':
          if (q.isSportsTGQuestion && q.name.includes('Country of Birth')) {
            return (
              <div key={q.sfid} className={styles.nameContainer}>
                <CountrySelect
                  countryLabelType="label"
                  errorText={errorText}
                  fullWidth
                  isClearable
                  name={q.sfid}
                  isRequired={question.isMandatory}
                  label={question.text || q.text}
                  onBlur={() => onFieldTouched(q.sfid)}
                  onChange={data => onDataChange(q.sfid, get(data, 'selectedOption.value') || '', true)}
                  placeholder="Select Country"
                  value={value || ''}
                  withBorder
                  withFlags={false}
                  hierarchyCountryCode={hierarchyCountryCode}
                  isToolTip={!!question.questionHelpText}
                  toolTipText={question.questionHelpText}
                  id={q.sfid}
                />
              </div>
            )
          }
          if (question.options.length < 50) {
            const disabled = !value
              ? disableQuestions && !['Gender Identity', 'Gender'].includes(q.name)
              : disableQuestions && !['Gender Identity'].includes(q.name)

            return (
              <div key={q.sfid} className={styles.nameContainer}>
                <PickList
                  label={question.text || q.text}
                  name={q.sfid}
                  isMandatory={question.isMandatory}
                  disabled={disabled}
                  options={question.options || []}
                  onBlur={() => onFieldTouched(q.sfid)}
                  errorText={errorText}
                  onChange={val => onDataChange(q.sfid, val, true)}
                  value={value}
                  type={q.type}
                  helpText={question.questionHelpText}
                  id={q.sfid}
                  dataTestId={dataTestId}
                />
              </div>
            )
          } else {
            return (
              <SelectAutoComplete
                key={q.sfid}
                label={question.text || q.text}
                name={q.sfid}
                isRequired={question.isMandatory}
                options={question.options || []}
                placeholder="Please select"
                value={value}
                handleChange={val => onDataChange(q.sfid, val, true)}
                errorText={errorText}
                isToolTip={!!question.questionHelpText}
                toolTipText={question.questionHelpText}
                lazyLoad
                withBorder
                bordered
                id={q.sfid}
              />
            )
          }
        case 'TermsAndConditions':
          return (
            <>
              {termsAndConditionsCount[0].text === question.text && (
                <StyledTnCWrapper>
                  Terms and Conditions
                  <hr />
                </StyledTnCWrapper>
              )}
              <Tnc
                key={q.sfid}
                onChange={val => onDataChange(q.sfid, val, true)}
                onBlur={() => onFieldTouched(q.sfid)}
                value={value}
                name={q.sfid}
                label={question.text || q.text}
                helpText={question.questionHelpText}
                isMandatory={question.isMandatory}
                description={question.termsText}
                errorText={errorText}
                id={q.sfid}
              />
            </>
          )
        case 'Checkbox':
          return (
            <div key={q.sfid} className={styles.nameContainer}>
              <Checkbox
                onChange={val => onDataChange(q.sfid, val, true)}
                onBlur={() => onFieldTouched(q.sfid)}
                errorText={errorText}
                name={q.sfid}
                label={question.text || q.text}
                value={value}
                isMandatory={question.isMandatory}
                helpText={question.questionHelpText}
                style={{ marginTop: -5 }}
                id={q.sfid}
                dataTestId={dataTestId}
              />
            </div>
          )
        case 'Document Upload':
          return (
            <div key={q.sfid} className={styles.standardContainer}>
              <FileUpload
                key={q.sfid}
                onFileDeleted={files => {
                  onDataChange(q.sfid, files, true)
                }}
                onFileUploaded={files => {
                  onDataChange(q.sfid, files, true)
                  setPending(false)
                }}
                onFileUploading={() => setPending(true)}
                label={question.text || q.text}
                formData={formData}
                value={value}
                name={q.sfid}
                isMandatory={question.isMandatory}
                errorText={errorText}
                isToolTip={!!question.questionHelpText}
                toolTipText={question.questionHelpText}
                id={q.sfid}
              />
            </div>
          )
        case 'Header Text':
          return (
            <div key={q.sfid}>
              <div
                className={classNames(styles.headerText, question.style ? ` ${styles.h2}` : '')}
                // biome-ignore lint/security/noDangerouslySetInnerHtml: TODO
                dangerouslySetInnerHTML={{
                  __html: sanitizeRichText(question.text || q.text),
                }}
              />
              {!question.style && <hr className={styles.divider} />}
            </div>
          )
        case 'Paragraph Text':
          return (
            <div
              key={q.sfid}
              className={styles.paraText}
              // biome-ignore lint/security/noDangerouslySetInnerHtml: TODO
              dangerouslySetInnerHTML={{
                __html: sanitizeRichText(question.text || q.text),
              }}
            />
          )
        case 'Search':
          return (
            <div key={q.sfid} className={styles.searchInput}>
              <div className={styles.searchInputContainer}>
                <InputField
                  withBorder
                  fullWidth
                  label={question.text || q.text}
                  name={q.sfid}
                  isRequired={question.isMandatory}
                  placeholder={question.text || q.text}
                  onChange={event => {
                    setSearchValue(event.target.value)
                  }}
                  value={value?.name || value}
                  onBlur={() => onFieldTouched(q.sfid)}
                  isToolTip={!!question.questionHelpText}
                  toolTipText={question.questionHelpText}
                  errorText={isEmpty(searchValues) && errorText}
                  id={q.sfid}
                  onKeyPress={handleSearchKeyPress}
                  dataTestId={dataTestId}
                />
                <div>
                  <PrimaryButton
                    onClick={() => {
                      searchOnClick(searchValue)
                      setSearchClicked(true)
                    }}
                    fullWidth
                    data-testid="findTeamButton"
                  >
                    Find
                  </PrimaryButton>
                </div>
              </div>
              {!isEmpty(searchValues) && (
                <div className={styles.nameContainer}>
                  <PickList
                    label="Register With"
                    name={q.sfid}
                    isMandatory={question.isMandatory}
                    disabled={disableQuestions && !['Gender Identity', 'Input Team Code'].includes(q.name)}
                    options={searchValues}
                    onBlur={() => onFieldTouched(q.sfid)}
                    errorText={errorText}
                    onChange={val => {
                      const selected = searchValues.find(s => s.name.toLowerCase() === val.toLowerCase())
                      onDataChange(q.sfid, selected, true)
                    }}
                    value={value?.value || value}
                    type="Picklist"
                    helpText={question.questionHelpText}
                    id={q.sfid}
                    showDescription
                    isTeamQuestion={q.isTeamQuestion}
                    dataTestId={dataTestId}
                  />
                </div>
              )}
              {isEmpty(searchValues) && searchClicked && <StyledNoResult>No Results</StyledNoResult>}
            </div>
          )
        default:
          return null
      }
    })
  }

  return (
    <Formik
      onSubmit={(values, { setSubmitting }) => {
        onSubmit(values)
        setSubmitting(true)
      }}
      initialValues={initValue}
      enableReinitialize
      validationSchema={validationSchema}
    >
      {({
        errors,
        touched,
        values,
        handleSubmit,
        resetForm,
        submitForm,
        handleReset,
        setFieldValue,
        setFieldTouched,
        setTouched,
        setSubmitting,
      }) => {
        if (isFunction(bindHandler)) {
          bindHandler({
            handleSubmit,
            resetForm,
            submitForm,
            handleReset,
            setSubmitting,
          })
        }
        return (
          <StyledForm>
            {renderComponents({
              setFieldValue,
              setFieldTouched,
              setTouched,
              setSubmitting,
              errors,
              touched,
              values,
            })}
            <FormikErrorFocus
              // See scroll-to-element for configuration options: https://www.npmjs.com/package/scroll-to-element
              offset={-150}
              align={'top'}
              focusDelay={0}
              ease={'in-out-quad'}
              duration={250}
            />
          </StyledForm>
        )
      }}
    </Formik>
  )
}

export default QuestionForm
